import { Injectable } from '@angular/core';
import { OAuth2Tokens, TokenStorage } from '@forgerock/javascript-sdk';
import { Store } from '@ngrx/store';
import { summariesQuery } from 'libs/policy/data-access/src/lib/+state/summaries/summaries.selectors';
import { SourceSystemInfo } from 'libs/shared/store/src/lib/component-config/component-config.interface';
import { filter, firstValueFrom } from 'rxjs';

import { env } from '@amfam/environment';
import { EnvironmentEnum, PolicyView, TypeOfPolicy } from '@amfam/shared/models';
import { ComponentConfig, selectComponentConfig } from '@amfam/shared/store';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { CopyService, WindowRef } from '@amfam/shared/utility/shared-services';

@Injectable({
  providedIn: 'root'
})
export class PolicyViewService {
  constructor(
    private store: Store,
    private windowRef: WindowRef,
    private copyService: CopyService
  ) {}

  async buildPolicyView(
    policyNumber: string,
    vehicleId: string | null,
    type: TypeOfPolicy
  ): Promise<PolicyView> {
    try {
      let tokens;
      try {
        tokens = (await TokenStorage.get()) as OAuth2Tokens;
        if (!tokens) {
          // Refresh the forgerock tokens
          alert('Refresh tokens!');
        }
      } catch (error) {
        // Refresh the tokens
        alert('Refresh tokens!');
      }
      const sourceId = 'AFI_MyAccount';
      const partnerId = await firstValueFrom(this.store.select(BrandSelectors.selectPartnerId));
      const policyView: PolicyView = {
        partnerId: partnerId === 'AFI' ? '642' : partnerId,
        policyNumber,
        token: tokens?.accessToken,
        type,
        environment: ['staging', 'prod'].includes(env.environment)
          ? EnvironmentEnum.prod
          : EnvironmentEnum.preprod,
        sourceId,
        showPolicyDocuments: true,
        showLoadingOverlay: true
      };
      if (vehicleId) policyView.vehicleId = vehicleId;
      return policyView;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  sendToApp(event) {
    console.log('Emit event to webview : ', event);
    this.sendToIos(event);
    this.sendToAndroid(event);
  }

  sendToIos(event) {
    (this.windowRef.nativeWindow as any).webkit?.messageHandlers?.nativeHandler?.postMessage(
      JSON.stringify(event)
    );
  }

  sendToAndroid(event) {
    (this.windowRef.nativeWindow as any).AndroidInterface?.sendData(JSON.stringify(event));
  }

  showPolicyView(type: 'auto' | 'home' | 'umbrella', componentInfo: SourceSystemInfo) {
    return (componentInfo?.policyView || []).includes(type);
  }

  async showSharedComponent(
    policyNumber: string,
    type: 'auto' | 'home' | 'umbrella',
    component: string
  ): Promise<boolean> {
    try {
      const sourceSystem = await firstValueFrom(
        this.store
          .select(summariesQuery.selectSourceSystem(policyNumber))
          .pipe(filter(pId => !!pId))
      );
      const config: ComponentConfig = await firstValueFrom(
        this.store.select(selectComponentConfig)
      );
      const componentConfig = config[component];
      const policyTypes = componentConfig[sourceSystem.toUpperCase()];
      return policyTypes.includes(type);
    } catch (error) {
      return null;
    }
  }
}
